import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useGetDotfilesConfiguration, useSetDotfilesConfiguration } from "@/queries/user-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC } from "react";
import { useDefaultEditor, useSetDefaultEditor } from "@/hooks/use-default-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useListEditors } from "@/queries/editor-queries";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { useDocumentTitle } from "@/hooks/use-document-title";

export const UserPreferencesPage: FC = () => {
    useDocumentTitle("Preferences");

    return (
        <div className="flex max-w-[580px] flex-col gap-6" data-testid="user-preferences-page">
            <Appearance />
            <DefaultEditor />
            <DotfilesConfiguration />
        </div>
    );
};

const Appearance: FC = () => {
    return (
        <InputField label="Appearance" hint="Customize how Gitpod looks on your device">
            <ThemeToggle />
        </InputField>
    );
};

const DefaultEditor: FC = () => {
    const setDefaultEditor = useSetDefaultEditor();
    const { editorId, isPending: isPendingDefaultEditor } = useDefaultEditor();
    const { data: editors, isPending: isPendingEditors } = useListEditors();

    const isPending = isPendingDefaultEditor || isPendingEditors;
    const selectedEditor = editors?.editors.find((editor) => editor.id === editorId) || editors?.editors[0];

    return (
        <InputField
            label="Default Editor"
            hint={
                <span>
                    This will be your default IDE on new projects.{" "}
                    <ExternalLink href={"https://www.gitpod.io/docs/flex/editors"} className="text-sm">
                        Learn more
                    </ExternalLink>.
                </span>
            }
        >
            <Select
                name="defaultEditor"
                value={selectedEditor?.id}
                onValueChange={(editorId) => setDefaultEditor(editorId)}
            >
                <SelectTrigger loading={isPending}>
                    <SelectValue>
                        <div className="flex items-center gap-2">
                            <EditorIcon editor={selectedEditor!} size="base" />
                            <span>{selectedEditor?.name}</span>
                        </div>
                    </SelectValue>
                    <SelectContent>
                        {editors?.editors.map((editor) => (
                            <SelectItem key={editor.id} value={editor.id}>
                                <div className="flex items-center gap-2">
                                    <EditorIcon editor={editor} size="base" />
                                    <span>{editor.name}</span>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </SelectTrigger>
            </Select>
        </InputField>
    );
};

const DotfilesConfiguration: FC = () => {
    const { data: dotfilesConfiguration, isPending: isPendingDotfilesConfiguration } = useGetDotfilesConfiguration();
    const setDotfilesConfiguration = useSetDotfilesConfiguration();
    const { toast } = useToast();

    const [dotfilesRepositoryError, setDotfilesRepositoryError] = useState<string | null>(null);
    const [dotfilesRepository, setDotfilesRepository] = useState("");
    const [savedDotfilesRepository, setSavedDotfilesRepository] = useState("");
    useEffect(() => {
        if (isPendingDotfilesConfiguration) {
            return;
        }
        setDotfilesRepository(dotfilesConfiguration?.repository || "");
        setSavedDotfilesRepository(dotfilesConfiguration?.repository || "");
    }, [dotfilesConfiguration?.repository, isPendingDotfilesConfiguration]);

    const updateDotfilesConfiguration = useCallback(
        async (repositoryUrl: string) => {
            // If the repository URL hasn't changed, don't try to update it
            // E.g. if focus and de-focus the input, then nothing should happen.
            if (savedDotfilesRepository === repositoryUrl) {
                return;
            }

            try {
                await setDotfilesConfiguration.mutateAsync({ repositoryUrl });
                setSavedDotfilesRepository(repositoryUrl);
                toast({ title: "Dotfiles repository updated" });
            } catch (error) {
                toast({
                    title: "Failed to update dotfiles repository",
                    description: formatError(error),
                });
            }
        },
        [savedDotfilesRepository, setDotfilesConfiguration, toast],
    );

    return (
        <InputField
            label="Dotfiles repository"
            error={dotfilesRepositoryError}
            hint={
                <span>
                    URL of a dotfiles Git repository.{" "}
                    <ExternalLink href={"https://www.gitpod.io/docs/flex/configuration/dotfiles"} className="text-sm">
                        Learn more
                    </ExternalLink>
                    .
                </span>
            }
        >
            <Input
                disabled={isPendingDotfilesConfiguration || setDotfilesConfiguration.isPending}
                value={dotfilesRepository}
                type="url"
                onChange={(e) => setDotfilesRepository(e.target.value)}
                data-testid="dotfiles-configuration-input"
                onBlur={(e) => {
                    if (e.target instanceof HTMLInputElement) {
                        const isValid = e.target.checkValidity();
                        setDotfilesRepositoryError(isValid ? null : "Invalid repository URL");
                        if (isValid) {
                            void updateDotfilesConfiguration(dotfilesRepository ?? "");
                        }
                    }
                }}
            />
        </InputField>
    );
};
